import { useItineraryTripActions } from '../../ItineraryTripProvider';
import {
  useSemItineraryMutation,
  useSemItinerarySelection,
  useSemTrackingInfoSelection,
} from './';
import { useFeeType } from 'clientState/providers/FeeTypeProvider';
import { useItinerarySelectionActions } from '../../ShoppingBundleProvider/ActionsProvider';
import { useUrlParams } from 'clientState/hooks/useUrlParams';
import { useSemLocations } from 'clientState/providers/SEM/SemLocationsProvider';
import { usePerformedSemSearchHash } from 'clientState/providers/SEM/SemSearchRequestHashProvider';

import { buildSemSelectionQuery, buildRecoverRequest } from './parsers';
import { UseMutationResult } from '@edreamsodigeo/frontend-api-client';
import {
  SemItinerarySelectionMutation,
  SemItinerarySelectionMutationVariables,
} from 'generated/graphql';
import { useTracking } from '@edreamsodigeo/frontend-core';

type SemFee = {
  price: number;
  type: string;
};

type SemFeeActions = {
  showSemItineraryTrip: (
    semItinerary: any,
    semItineraryPosition: number | undefined,
    request: any
  ) => void;
};

const getFee = (semItinerary, feeTypeId): SemFee =>
  semItinerary.fees.find(({ type }) => type.id === feeTypeId);

const getSelectedItinerary = (semItinerary, feeTypeId): any => {
  if (!feeTypeId) {
    return semItinerary;
  }
  const fee = getFee(semItinerary, feeTypeId);
  return { ...semItinerary, price: fee?.price, feeType: fee?.type };
};

export const useSemItinerarySelectionActions = (): SemFeeActions => {
  const { open } = useItineraryTripActions() || {};
  const setSemItineraryInfo = useSemItinerarySelection();
  const setSemTrackingInfo = useSemTrackingInfoSelection();
  const { recoverSearchRequest } = useItinerarySelectionActions() || {};
  const hash = usePerformedSemSearchHash();
  const params = useUrlParams(hash);
  const { locations } = useSemLocations() || {};
  const { mutate } = useSemItineraryMutation() || {};

  const feeType = useFeeType();

  const showSemItineraryTrip = (
    semItinerary,
    semItineraryPosition,
    request
  ): void => {
    const feeTypeId = request.feeTypeId ?? feeType.id;
    const selectedSemItinerary = getSelectedItinerary(semItinerary, feeTypeId);
    recoverSearchRequest?.(
      buildRecoverRequest(semItinerary, params, locations)
    );

    open();
    setSemItineraryInfo({
      semItinerary: selectedSemItinerary,
      semItineraryPosition,
    });
    setSemTrackingInfo({ returningUserDeviceId: '' });
    mutate?.({
      semItinerarySelectionRequest: buildSemSelectionQuery(
        semItinerary,
        params,
        locations
      ),
    });
  };

  return {
    showSemItineraryTrip,
  };
};

export const useSemSelectTrackErrors = (
  mutationResult: UseMutationResult<
    SemItinerarySelectionMutation,
    SemItinerarySelectionMutationVariables
  >
): void => {
  const { trackEvent } = useTracking();
  if (
    mutationResult.status !== 'idle' &&
    !mutationResult.loading &&
    mutationResult.errors
  ) {
    trackEvent('request_errors', 'INT-003-code:precreate_shopping_cart_error');
  }
};
